/**
 * AetherLite uploads endpoint — sube imágenes (logo, fotos de coach / cliente).
 *
 * Backend: POST /aetherlite/upload  (multipart/form-data)
 *   field "file"   (image/jpeg | image/png | image/webp, máx 5 MB)
 *   field "folder" ("brand" | "client")
 * Auth: Bearer JWT de AetherLite (mismo que apiFetch agrega).
 *
 * NOTA: usamos `fetch` directo en lugar de apiFetch porque:
 *   - El handler 401 de apiFetch ya cubre el caso (lo replicamos aquí
 *     manualmente porque no podemos importar el dispatcher sin acoplamiento
 *     extra) — en este caso lanzamos el ApiError y el AuthProvider escucha
 *     el evento global cuando apiFetch lo dispare desde otra ruta.
 *   - Necesitamos control fino sobre los headers para garantizar que el
 *     navegador inyecte el `multipart/form-data; boundary=…` automáticamente
 *     (apiFetch lo hace bien, pero seguimos el patrón de admin.exportCsv()).
 *
 * Implementado en backend: aethercoach-mvp/backend/routers/aetherlite_uploads.py
 */
import { ApiError, apiBaseUrl, getToken } from "./client";

export interface UploadResponse {
  /** URL absoluta lista para guardar en BD (ej: https://aethercoach.nilabs.co/uploads/aetherlite/123/brand_xxx.jpg). */
  url: string;
  /** Path relativo al mount StaticFiles (ej: /uploads/aetherlite/123/brand_xxx.jpg). */
  path: string;
  file_size_kb: number;
  content_type: string;
}

/**
 * Sube una imagen al backend AetherLite.
 *
 * @param file   Blob/File con la imagen recortada (image/jpeg | image/png | image/webp).
 * @param folder "brand" para coach (foto perfil + logo) | "client" para foto de cliente.
 * @returns UploadResponse con la URL pública absoluta.
 * @throws ApiError con el detail del backend si el upload falla
 *   (400 tipo no permitido, 413 >5MB, 401 sin auth).
 */
export async function uploadImage(
  file: Blob,
  folder: "brand" | "client",
): Promise<UploadResponse> {
  const fd = new FormData();
  // Si Blob no tiene name (ej. canvas.toBlob), inventamos uno con extensión correcta.
  const guessedExt = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
  const fileName = (file instanceof File && file.name) ? file.name : `upload.${guessedExt}`;
  fd.append("file", file, fileName);
  fd.append("folder", folder);

  const token = getToken();
  const res = await fetch(`${apiBaseUrl()}/aetherlite/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });

  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      try { detail = await res.text(); } catch { detail = null; }
    }
    const msg =
      detail && typeof detail === "object" && "detail" in detail
        ? String((detail as { detail: unknown }).detail)
        : res.statusText || `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, detail);
  }

  return (await res.json()) as UploadResponse;
}
